import { locale } from '@trqk/utils';

import { URL_PARAMS } from '../../../../../constants';

export const filterWidth = 200;
export const filterHeight = 45;
export const minWidthOffset = 40;

export const columnIds = {
    program: 'program',
    workTitle: 'workTitle',
    workId: 'workId',
    paymentType: 'paymentType',
    publisher: 'publisher',
    writer: 'writer',
    dayPart: 'dayPart',
    musicUserGenre: 'musicUserGenre',
    source: 'source',
    duration: 'duration',
    countryCode: 'countryCode',
    plays: 'plays',
    earnings: 'earnings',
};

export const columnWidthInputs = {
    [columnIds.program]: {
        min: 80,
    },
    [columnIds.workTitle]: {
        min: 80,
    },
    [columnIds.workId]: {
        min: 60,
        max: 80,
    },
    [columnIds.paymentType]: {
        min: 105,
        max: 125,
    },
    [columnIds.publisher]: {
        min: 70,
        max: 150,
    },
    [columnIds.writer]: {
        min: 70,
        max: 150,
    },
    [columnIds.dayPart]: {
        min: 85,
        max: 85,
    },
    [columnIds.musicUserGenre]: {
        min: 100,
        max: 100,
    },
    [columnIds.source]: {
        min: 70,
        max: 150,
    },
    [columnIds.duration]: {
        min: 80,
        max: 80,
    },
    [columnIds.countryCode]: {
        min: 60,
        max: 60,
    },
    [columnIds.plays]: {
        min: 50,
        max: 50,
    },
    [columnIds.earnings]: {
        min: 80,
        max: 80,
    },
};

export const emptyEarningsPreFilterValue: EarningsPreFilterValue = [
    -Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
];

/**
 * The first value of each tuple here is a filter ID, which is independent of column IDs. Columns
 * can display more than one field in the data; filters can only map to a single field in the data.
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- see comment next to `episode: null` below
// @ts-ignore
export const preFilterConfigs: Map<string, PreFilterConfig> = new Map([
    [
        'show',
        {
            id: 'show',
            emptyOption: {
                id: 'performances.allShows',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.SHOWS.ALL,
                value: {
                    show: null,
                },
            },
            fieldValuePath: ['Show'],
            filterType: 'equals',
            filterValuePath: ['value', 'show'],
            urlParamKey: URL_PARAMS.PROGRAM,
            renderType: 'dropdown',
        },
    ],
    [
        'episode',
        {
            id: 'episode',
            emptyOption: {
                id: 'performances.allEpisodes',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EPISODES.ALL,
                value: {
                    episode: null, // for some reason, tsc is complaining because `show` is undefined here, even though that makes emptyOption a valid DropdownInputValue
                },
            },
            fieldValuePath: ['Episode'],
            filterType: 'equals',
            filterValuePath: ['value', 'episode'],
            urlParamKey: URL_PARAMS.EPISODE,
            renderType: 'dropdown',
        },
    ],
    [
        'workTitle',
        {
            id: 'workTitle',
            emptyOption: {
                id: 'performances.allWorkTitles',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.WORK_TITLES
                    .ALL,
                value: {
                    workTitle: null,
                },
            },
            fieldValuePath: ['Work Title'],
            filterType: 'equals',
            filterValuePath: ['value', 'workTitle'],
            urlParamKey: URL_PARAMS.WORK_TITLE,
            renderType: 'dropdown',
        },
    ],
    [
        'paymentType',
        {
            id: 'paymentType',
            emptyOption: {
                id: 'performances.allPaymentTypes',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.PAYMENT_TYPES
                    .ALL,
                value: {
                    paymentType: null,
                },
            },
            fieldValuePath: ['paymentType'],
            filterType: 'equals',
            filterValuePath: ['value', 'paymentType'],
            urlParamKey: URL_PARAMS.PAYMENT_TYPE,
            renderType: 'dropdown',
        },
    ],
    [
        'publisher',
        {
            id: 'publisher',
            emptyOption: {
                id: 'performances.allPublishers',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.PUBLISHERS
                    .ALL,
                value: {
                    publisher: null,
                },
            },
            fieldValuePath: ['publisher'],
            filterType: 'equals',
            filterValuePath: ['value', 'publisher'],
            urlParamKey: URL_PARAMS.PUBLISHER,
            renderType: 'dropdown',
        },
    ],
    [
        'writer',
        {
            id: 'writer',
            emptyOption: {
                id: 'performances.allWriters',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.WRITERS.ALL,
                value: {
                    writer: null,
                },
            },
            fieldValuePath: ['writer'],
            filterType: 'equals',
            filterValuePath: ['value', 'writer'],
            urlParamKey: URL_PARAMS.WRITER,
            renderType: 'dropdown',
        },
    ],
    [
        'dayPart',
        {
            id: 'dayPart',
            emptyOption: {
                id: 'performances.allDayParts',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.DAY_PART.ALL,
                value: {
                    dayPart: null,
                },
            },
            fieldValuePath: ['Day Part'],
            filterType: 'equals',
            filterValuePath: ['value', 'dayPart'],
            urlParamKey: URL_PARAMS.DAY_PART,
            renderType: 'dropdown',
        },
    ],
    [
        'musicUserGenre',
        {
            id: 'musicUserGenre',
            emptyOption: {
                id: 'performances.allMusicUserGenres',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS
                    .MUSIC_USER_GENRE.ALL,
                value: {
                    musicUserGenre: null,
                },
            },
            fieldValuePath: ['Music User Genre'],
            filterType: 'equals',
            filterValuePath: ['value', 'musicUserGenre'],
            urlParamKey: URL_PARAMS.MEDIA,
            renderType: 'dropdown',
        },
    ],
    [
        'source',
        {
            id: 'source',
            emptyOption: {
                id: 'performances.allSources',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.SOURCES.ALL,
                value: {
                    source: null,
                },
            },
            fieldValuePath: ['Source'],
            filterType: 'equals',
            filterValuePath: ['value', 'source'],
            urlParamKey: URL_PARAMS.CHANNEL,
            renderType: 'dropdown',
        },
    ],
    [
        'countryCode',
        {
            id: 'countryCode',
            emptyOption: {
                id: 'performances.allCountryCodes',
                label: locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.COUNTRY_CODE
                    .ALL,
                value: {
                    countryCode: null,
                },
            },
            fieldValuePath: ['Country Code'],
            filterType: 'equals',
            filterValuePath: ['value', 'countryCode'],
            urlParamKey: URL_PARAMS.TERRITORY,
            renderType: 'dropdown',
        },
    ],
    [
        'earningsMax',
        {
            id: 'earningsMax',
            fieldValuePath: ['Earnings'],
            filterType: 'lessThanOrEqualTo',
            filterValuePath: ['value', 'Earnings'],
            urlParamKey: URL_PARAMS.MAX_EARNINGS,
            renderType: 'earnings',
            earningsType: 'max',
        },
    ],
    [
        'earningsMin',
        {
            id: 'earningsMin',
            fieldValuePath: ['Earnings'],
            filterType: 'greaterThanOrEqualTo',
            filterValuePath: ['value', 'Earnings'],
            urlParamKey: URL_PARAMS.MIN_EARNINGS,
            renderType: 'earnings',
            earningsType: 'min',
        },
    ],
]);
